import type { DocumentKind } from './library.js';
import type { RevisionState } from './revision-state.js';

/** Yürürlükte olmayan revizyonun dosya adındaki etiketi (ADR 0028 #30 E). */
const STATE_LABEL: Readonly<Record<RevisionState, string | null>> = {
  published: null,
  superseded: 'SUPERSEDED',
  withdrawn: 'WITHDRAWN',
  scheduled: 'NOT YET EFFECTIVE',
  cancelled: 'CANCELLED',
  draft: 'DRAFT',
  pending_approval: 'DRAFT',
  approved: 'DRAFT',
  discarded: 'DRAFT',
};

/**
 * Yürürlükte olmayan revizyonun etiketi ("SUPERSEDED", "WITHDRAWN", "NOT YET EFFECTIVE",
 * "DRAFT"); yürürlükteyse `null`. Dosya adı, önizleme filigranı ve basılı kopya aynı etiketi
 * kullanır (ADR 0028 #30).
 */
export function revisionStateLabel(state: RevisionState): string | null {
  return STATE_LABEL[state];
}

const safe = (value: string) => value.replace(/[\\/:*?"<>|]/g, '_').trim();

/**
 * İndirilen ana dosyanın adı (ADR 0028 #30 C, E): "Form 01 - Safety Management System (Rev 1.0)
 * .docx". Yürürlükte olmayan revizyon durumunu taşır ("… SUPERSEDED.docx"); baytlar değişmez.
 * Windows'ta geçersiz karakterler `_` olur.
 */
export function downloadFileName(input: {
  kind: DocumentKind;
  docNo: string;
  title: string;
  revisionNo: string;
  /** Yüklenen dosyanın adı; uzantı buradan. */
  fileName: string;
  state: RevisionState;
}): string {
  const dot = input.fileName.lastIndexOf('.');
  const extension = dot < 0 ? '' : input.fileName.slice(dot).toLowerCase();
  const number = input.kind === 'form' ? `Form ${input.docNo}` : input.docNo;
  const revision = /^rev/i.test(input.revisionNo) ? input.revisionNo : `Rev ${input.revisionNo}`;
  const label = STATE_LABEL[input.state];
  return `${safe(`${number} - ${input.title} (${revision})${label ? ` ${label}` : ''}`)}${extension}`;
}

/**
 * Ekin ya da baskı PDF'inin indirilen adı (ADR 0028 #30 E, #34): revizyon yürürlükteyse yüklenen
 * ad; değilse aynı etiket uzantıdan önce: "Print (Rev 1.0) SUPERSEDED.pdf". Ekler onaylanan
 * içeriğin parçasıdır; eski ya da onaylanmamış revizyonun basılacak PDF'i güncel görünmemeli
 * [ISM 11.2.3].
 */
export function attachmentDownloadName(input: {
  fileName: string;
  revisionNo: string;
  state: RevisionState;
}): string {
  const label = STATE_LABEL[input.state];
  if (!label) return input.fileName;
  const dot = input.fileName.lastIndexOf('.');
  const base = dot <= 0 ? input.fileName : input.fileName.slice(0, dot);
  const extension = dot <= 0 ? '' : input.fileName.slice(dot).toLowerCase();
  const revision = /^rev/i.test(input.revisionNo) ? input.revisionNo : `Rev ${input.revisionNo}`;
  return `${safe(`${base} (${revision}) ${label}`)}${extension}`;
}
